import { supabase } from '../lib/supabase';
import { attendanceIntegrationService } from './attendanceIntegrationService';
import { contractService } from './contractService';

export const timesheetService = {
  // ============================================================================
  // TIMESHEET RECORDS MANAGEMENT
  // ============================================================================

  async getAllTimesheetRecords(startDate = null, endDate = null, department = null) {
    // Fix: Ensure null values are passed instead of "null" strings or empty strings
    const validStartDate = startDate && startDate !== 'null' && startDate?.trim() !== '' ? startDate : null;
    const validEndDate = endDate && endDate !== 'null' && endDate?.trim() !== '' ? endDate : null;
    const validDepartment = department && department !== 'null' && department?.trim() !== '' ? department : null;

    let query = supabase?.from('daily_time_entries')?.select(`
        *,
        employees (
          employee_number,
          first_name,
          last_name,
          department,
          position
        ),
        pay_periods (
          ppe_number,
          period_start,
          period_end
        )
      `)?.order('entry_date', { ascending: false });

    if (validStartDate) {
      query = query?.gte('entry_date', validStartDate);
    }

    if (validEndDate) {
      query = query?.lte('entry_date', validEndDate);
    }

    if (validDepartment) {
      query = query?.eq('employees.department', validDepartment);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    
    // Fix: Map database fields correctly to component expectations
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name,
        department: row?.employees?.department,
        position: row?.employees?.position
      } : null,
      clockIn: row?.clock_in_time,
      clockOut: row?.clock_out_time,
      clockInTime: row?.clock_in_time,
      clockOutTime: row?.clock_out_time,
      totalHours: row?.total_hours || 0,
      regularHours: row?.regular_hours || 0,
      overtimeHours: row?.overtime_1_5x_hours || 0,
      sundayHours: row?.sunday_2x_hours || 0,
      attendanceStatus: row?.overtime_1_5x_hours > 0 || row?.sunday_2x_hours > 0 ? 'overtime' : 'present',
      status: 'present',
      notes: row?.notes || '',
      entryDate: row?.entry_date,
      attendanceDate: row?.entry_date,
      biometricVerificationMethod: row?.biometric_verification_method || null,
      location: row?.location || 'Office',
      createdAt: row?.created_at
    })) || [];
  },

  async getEmployeeTimesheets(employeeId, startDate, endDate) {
    return await attendanceIntegrationService?.getAttendanceRecords(employeeId, startDate, endDate);
  },

  async createManualTimeEntry(entry) {
    // Calculate hours
    const clockIn = new Date(entry?.clockInTime);
    const clockOut = new Date(entry?.clockOutTime);
    const totalHours = (clockOut - clockIn) / (1000 * 60 * 60);

    // Get employee contract for standard hours
    const contracts = await contractService?.getByEmployeeId(entry?.employeeId);
    const activeContract = contracts?.find(c => c?.contractStatus === 'active');
    const standardHours = activeContract?.standardHours || 8;

    // Calculate regular and overtime hours
    const regularHours = Math.min(totalHours, standardHours);
    const overtimeHours = Math.max(0, totalHours - standardHours);

    const record = {
      employeeId: entry?.employeeId,
      attendanceSystemId: entry?.attendanceSystemId || null,
      biometricRegistrationId: null, // Manual entry has no biometric
      attendanceDate: entry?.attendanceDate,
      clockInTime: entry?.clockInTime,
      clockOutTime: entry?.clockOutTime,
      totalHours: totalHours?.toFixed(2),
      regularHours: regularHours?.toFixed(2),
      overtimeHours: overtimeHours?.toFixed(2),
      attendanceStatus: overtimeHours > 0 ? 'overtime' : 'present',
      biometricVerificationMethod: null,
      location: entry?.location || 'Manual Entry',
      notes: entry?.notes || 'Manual time entry by timekeeper',
      rawData: { manualEntry: true, enteredBy: entry?.enteredBy }
    };

    return await attendanceIntegrationService?.createAttendanceRecord(record);
  },

  async updateTimeEntry(recordId, updates) {
    const updateData = {
      updated_at: new Date()?.toISOString()
    };

    if (updates?.clockInTime) updateData.clock_in_time = updates?.clockInTime;
    if (updates?.clockOutTime) updateData.clock_out_time = updates?.clockOutTime;
    if (updates?.totalHours) updateData.total_hours = updates?.totalHours;
    if (updates?.regularHours) updateData.regular_hours = updates?.regularHours;
    if (updates?.overtimeHours) updateData.overtime_hours = updates?.overtimeHours;
    if (updates?.attendanceStatus) updateData.attendance_status = updates?.attendanceStatus;
    if (updates?.notes) updateData.notes = updates?.notes;

    const { data, error } = await supabase
      ?.from('attendance_records')
      ?.update(updateData)
      ?.eq('id', recordId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      totalHours: data?.total_hours,
      overtimeHours: data?.overtime_hours,
      updatedAt: data?.updated_at
    };
  },

  async deleteTimeEntry(recordId) {
    const { error } = await supabase?.from('timesheet_records')?.delete()?.eq('id', recordId);

    if (error) throw error;
  },

  // Generate demo data for testing
  async generateDemoData(startDate, endDate) {
    try {
      // Get active employees
      const { data: employees, error: empError } = await supabase?.from('employees')?.select('id, employee_number, first_name, last_name, department')?.eq('is_active', true)?.limit(10);

      if (empError) throw empError;
      if (!employees || employees?.length === 0) {
        throw new Error('No active employees found. Please add employees first.');
      }

      // Generate timesheet records for each employee for each day
      const demoRecords = [];
      const start = new Date(startDate);
      const end = new Date(endDate);

      for (let d = new Date(start); d <= end; d?.setDate(d?.getDate() + 1)) {
        // Skip weekends
        if (d?.getDay() === 0 || d?.getDay() === 6) continue;

        for (const employee of employees) {
          // Random attendance (90% present)
          const isPresent = Math.random() > 0.1;
          
          if (isPresent) {
            // Random clock in time between 7:30 AM and 9:00 AM
            const clockInHour = 7 + Math.floor(Math.random() * 2);
            const clockInMinute = Math.random() > 0.5 ? 30 : 0;
            const clockInTime = new Date(d);
            clockInTime?.setHours(clockInHour, clockInMinute, 0, 0);

            // Random clock out time between 4:30 PM and 6:00 PM
            const clockOutHour = 16 + Math.floor(Math.random() * 2);
            const clockOutMinute = Math.random() > 0.5 ? 30 : 0;
            const clockOutTime = new Date(d);
            clockOutTime?.setHours(clockOutHour, clockOutMinute, 0, 0);

            // Calculate hours
            const totalHours = (clockOutTime - clockInTime) / (1000 * 60 * 60);
            const regularHours = Math.min(totalHours, 8);
            const overtimeHours = Math.max(totalHours - 8, 0);

            demoRecords?.push({
              employee_id: employee?.id,
              entry_date: d?.toISOString()?.split('T')?.[0],
              clock_in_time: clockInTime?.toISOString(),
              clock_out_time: clockOutTime?.toISOString(),
              total_hours: totalHours,
              regular_hours: regularHours,
              overtime_1_5x_hours: overtimeHours,
              sunday_2x_hours: 0,
              location: Math.random() > 0.5 ? 'Main Office' : 'Remote',
              notes: 'Demo data for testing',
              biometric_verification_method: null
            });
          }
        }
      }

      // Insert demo records into daily_time_entries table
      const { error: insertError } = await supabase?.from('daily_time_entries')?.insert(demoRecords);

      if (insertError) throw insertError;

      return {
        success: true,
        recordsGenerated: demoRecords?.length,
        employeesIncluded: employees?.length,
        dateRange: { start: startDate, end: endDate }
      };
    } catch (error) {
      console.error('Error generating demo data:', error);
      throw error;
    }
  },

  // ============================================================================
  // OVERTIME CALCULATIONS
  // ============================================================================

  async calculateOvertimePayroll(employeeId, startDate, endDate) {
    // Get attendance records
    const records = await this?.getEmployeeTimesheets(employeeId, startDate, endDate);
    
    // Get employee contract
    const contracts = await contractService?.getByEmployeeId(employeeId);
    const activeContract = contracts?.find(c => c?.contractStatus === 'active');
    
    if (!activeContract) {
      throw new Error('No active contract found for employee');
    }

    const hourlyRate = activeContract?.hourlyRate || 0;
    const overtimeMultiplier = activeContract?.overtimeMultiplier || 1.5;
    
    // Calculate totals
    let totalRegularHours = 0;
    let totalOvertimeHours = 0;
    let totalSundayHours = 0;

    records?.forEach(record => {
      const recordDate = new Date(record?.attendanceDate);
      const isSunday = recordDate?.getDay() === 0;

      totalRegularHours += parseFloat(record?.regularHours || 0);
      
      if (isSunday) {
        // Sunday premium: 2x rate
        totalSundayHours += parseFloat(record?.overtimeHours || 0);
      } else {
        // Regular overtime: 1.5x rate (or contract multiplier)
        totalOvertimeHours += parseFloat(record?.overtimeHours || 0);
      }
    });

    const regularPay = totalRegularHours * hourlyRate;
    const overtimePay = totalOvertimeHours * hourlyRate * overtimeMultiplier;
    const sundayPay = totalSundayHours * hourlyRate * 2.0; // Sunday is always 2x
    const totalPay = regularPay + overtimePay + sundayPay;

    return {
      employeeId,
      periodStart: startDate,
      periodEnd: endDate,
      hourlyRate,
      overtimeMultiplier,
      totalRegularHours: totalRegularHours?.toFixed(2),
      totalOvertimeHours: totalOvertimeHours?.toFixed(2),
      totalSundayHours: totalSundayHours?.toFixed(2),
      regularPay: regularPay?.toFixed(2),
      overtimePay: overtimePay?.toFixed(2),
      sundayPay: sundayPay?.toFixed(2),
      totalPay: totalPay?.toFixed(2),
      recordCount: records?.length
    };
  },

  async calculateBulkOvertimePayroll(startDate, endDate, department = null) {
    // Get all employees
    const { data: employees, error } = await supabase
      ?.from('employees')
      ?.select('id, employee_number, first_name, last_name, department')
      ?.eq('is_active', true);

    if (error) throw error;

    const filteredEmployees = department 
      ? employees?.filter(e => e?.department === department)
      : employees;

    const calculations = [];

    for (const employee of filteredEmployees || []) {
      try {
        const calc = await this?.calculateOvertimePayroll(employee?.id, startDate, endDate);
        calculations?.push({
          ...calc,
          employeeNumber: employee?.employee_number,
          employeeName: `${employee?.first_name} ${employee?.last_name}`,
          department: employee?.department
        });
      } catch (err) {
        console.error(`Error calculating overtime for employee ${employee?.id}:`, err);
      }
    }

    return calculations;
  },

  // ============================================================================
  // TIME MACHINE INTEGRATION
  // ============================================================================

  async syncTimeMachineData(systemId) {
    // Delegate to Hikvision integration service for actual sync
    const hikvisionIntegrationService = require('./hikvisionIntegrationService')?.default;
    
    const system = await attendanceIntegrationService?.getAttendanceSystemById(systemId);
    
    if (!system?.isActive) {
      throw new Error('Attendance system is not active');
    }

    // Calculate date range (last 7 days)
    const endDate = new Date();
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - 7);
    
    const startDateStr = startDate?.toISOString()?.split('T')?.[0];
    const endDateStr = endDate?.toISOString()?.split('T')?.[0];

    // Use Hikvision service for sync
    const syncResults = await hikvisionIntegrationService?.syncAttendanceData(
      systemId,
      startDateStr,
      endDateStr
    );

    return syncResults;
  },

  calculateNextSync(frequency) {
    const now = new Date();
    switch (frequency) {
      case 'hourly':
        return new Date(now?.getTime() + 60 * 60 * 1000)?.toISOString();
      case 'daily':
        return new Date(now?.getTime() + 24 * 60 * 60 * 1000)?.toISOString();
      case 'weekly':
        return new Date(now?.getTime() + 7 * 24 * 60 * 60 * 1000)?.toISOString();
      default:
        return new Date(now?.getTime() + 24 * 60 * 60 * 1000)?.toISOString();
    }
  },

  // ============================================================================
  // TIME MACHINE DEVICE MANAGEMENT
  // ============================================================================

  async addTimeMachineDevice(deviceData) {
    const deviceConfig = {
      system_name: deviceData?.systemName,
      system_vendor: deviceData?.systemVendor,
      biometric_types: deviceData?.biometricTypes,
      integration_method: deviceData?.integrationMethod,
      api_endpoint: deviceData?.apiEndpoint,
      sync_frequency: deviceData?.syncFrequency,
      network_ip_address: deviceData?.networkIpAddress || null,
      network_port: deviceData?.networkPort || null,
      qr_code_data: deviceData?.qrCodeData || null,
      connection_type: deviceData?.connectionType || 'ip',
      connection_status: 'pending',
      last_sync_at: null,
      is_active: true
    };

    const { data, error } = await supabase
      ?.from('attendance_system_configs')
      ?.insert([deviceConfig])
      ?.select()
      ?.single();

    if (error) throw error;

    // Attempt to verify connection
    if (deviceData?.networkIpAddress) {
      await this.verifyDeviceConnection(data?.id);
    }

    return {
      id: data?.id,
      systemName: data?.system_name,
      systemVendor: data?.system_vendor,
      biometricTypes: data?.biometric_types,
      integrationMethod: data?.integration_method,
      networkIpAddress: data?.network_ip_address,
      networkPort: data?.network_port,
      connectionType: data?.connection_type,
      connectionStatus: data?.connection_status,
      isActive: data?.is_active,
      createdAt: data?.created_at
    };
  },

  async verifyDeviceConnection(deviceId) {
    try {
      // Get device configuration
      const { data: device, error: fetchError } = await supabase
        ?.from('attendance_system_configs')
        ?.select('*')
        ?.eq('id', deviceId)
        ?.single();

      if (fetchError) throw fetchError;

      // Simulate connection verification
      // In production, this would ping the device IP or test API endpoint
      const isConnected = await this.pingDevice(device?.network_ip_address, device?.network_port);

      // Update connection status
      const { error: updateError } = await supabase
        ?.from('attendance_system_configs')
        ?.update({
          connection_status: isConnected ? 'connected' : 'failed',
          last_sync_at: isConnected ? new Date()?.toISOString() : null
        })
        ?.eq('id', deviceId);

      if (updateError) throw updateError;

      return isConnected;
    } catch (error) {
      console.error('Device connection verification failed:', error);
      
      // Update status to failed
      await supabase
        ?.from('attendance_system_configs')
        ?.update({ connection_status: 'failed' })
        ?.eq('id', deviceId);
      
      return false;
    }
  },

  async pingDevice(ipAddress, port) {
    // Simulate network ping to device
    // In production, this would make actual HTTP request to device
    return new Promise((resolve) => {
      setTimeout(() => {
        // Simulate 90% success rate for demo
        resolve(Math.random() > 0.1);
      }, 1500);
    });
  },

  async syncDeviceData(deviceId) {
    try {
      const { data: device, error: fetchError } = await supabase
        ?.from('attendance_system_configs')
        ?.select('*')
        ?.eq('id', deviceId)
        ?.single();

      if (fetchError) throw fetchError;

      // Verify connection first
      const isConnected = await this.verifyDeviceConnection(deviceId);
      
      if (!isConnected) {
        throw new Error('Device is not connected');
      }

      // Fetch attendance data from device
      // In production, this would call device API or read from network endpoint
      const attendanceData = await this.fetchDeviceAttendanceData(
        device?.network_ip_address,
        device?.network_port,
        device?.api_endpoint
      );

      // Insert attendance records
      if (attendanceData?.length > 0) {
        const { error: insertError } = await supabase
          ?.from('attendance_records')
          ?.insert(attendanceData);

        if (insertError) throw insertError;
      }

      // Update last sync time
      await supabase
        ?.from('attendance_system_configs')
        ?.update({ last_sync_at: new Date()?.toISOString() })
        ?.eq('id', deviceId);

      return {
        success: true,
        recordsImported: attendanceData?.length || 0,
        syncedAt: new Date()?.toISOString()
      };
    } catch (error) {
      console.error('Device sync failed:', error);
      throw error;
    }
  },

  async fetchDeviceAttendanceData(ipAddress, port, apiEndpoint) {
    // Simulate fetching data from device
    // In production, this would make HTTP request to device API
    return new Promise((resolve) => {
      setTimeout(() => {
        // Return empty array for demo - real implementation would fetch actual data
        resolve([]);
      }, 1000);
    });
  },

  async parseQrCodeData(qrCodeString) {
    try {
      const qrData = JSON.parse(qrCodeString);
      
      return {
        deviceId: qrData?.deviceId,
        ipAddress: qrData?.ipAddress,
        port: qrData?.port,
        vendor: qrData?.vendor,
        model: qrData?.model,
        serialNumber: qrData?.serialNumber,
        firmwareVersion: qrData?.firmwareVersion,
        supportedBiometrics: qrData?.supportedBiometrics || []
      };
    } catch (error) {
      console.error('QR code parsing failed:', error);
      throw new Error('Invalid QR code format');
    }
  },

  async getDevicesByConnectionType(connectionType) {
    const { data, error } = await supabase
      ?.from('attendance_system_configs')
      ?.select('*')
      ?.eq('connection_type', connectionType)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(device => ({
      id: device?.id,
      systemName: device?.system_name,
      systemVendor: device?.system_vendor,
      biometricTypes: device?.biometric_types,
      networkIpAddress: device?.network_ip_address,
      networkPort: device?.network_port,
      connectionType: device?.connection_type,
      connectionStatus: device?.connection_status,
      lastSyncAt: device?.last_sync_at,
      isActive: device?.is_active,
      createdAt: device?.created_at
    })) || [];
  },

  // ============================================================================
  // STATISTICS AND ANALYTICS
  // ============================================================================

  async getTimesheetStatistics(startDate = null, endDate = null) {
    // Fix: Ensure null values are passed instead of "null" strings
    const validStartDate = startDate && startDate !== 'null' && startDate !== '' ? startDate : null;
    const validEndDate = endDate && endDate !== 'null' && endDate !== '' ? endDate : null;

    let query = supabase
      ?.from('daily_time_entries')
      ?.select('total_hours, regular_hours, overtime_1_5x_hours, sunday_2x_hours, entry_date, clock_in_time, clock_out_time');

    if (validStartDate) {
      query = query?.gte('entry_date', validStartDate);
    }

    if (validEndDate) {
      query = query?.lte('entry_date', validEndDate);
    }

    const { data, error } = await query;
    
    if (error) throw error;

    const totalRecords = data?.length || 0;
    let totalRegularHours = data?.reduce((sum, r) => sum + parseFloat(r?.regular_hours || 0), 0);
    let totalOvertimeHours = data?.reduce((sum, r) => sum + parseFloat(r?.overtime_1_5x_hours || 0), 0);
    const lateClockIns = data?.filter(r => {
      if (!r?.clock_in_time) return false;
      const clockIn = new Date(`${r?.entry_date}T${r?.clock_in_time}`);
      const standardStart = new Date(`${r?.entry_date}T09:00:00`);
      return clockIn > standardStart;
    })?.length || 0;
    const missingClockOuts = data?.filter(r => r?.clock_in_time && !r?.clock_out_time)?.length || 0;

    return {
      totalRecords,
      totalRegularHours: totalRegularHours?.toFixed(2),
      totalOvertimeHours: totalOvertimeHours?.toFixed(2),
      lateClockIns,
      missingClockOuts,
      averageHoursPerDay: totalRecords > 0 ? (totalRegularHours / totalRecords)?.toFixed(2) : '0.00'
    };
  },

  /**
   * Convert timesheet records to payroll entries
   * Automatically processes attendance data from Hikvision time machines
   * @param {string} payPeriodStart - Pay period start date
   * @param {string} payPeriodEnd - Pay period end date
   * @param {string} departmentId - Optional department filter
   * @returns {Promise<Object>} Conversion results
   */
  async convertTimesheetsToPayroll(payPeriodStart, payPeriodEnd, departmentId = null) {
    try {
      // Get all attendance records for the pay period
      let query = supabase?.from('attendance_records')?.select(`
          *,
          employees (
            id,
            first_name,
            last_name,
            employee_number,
            department_id,
            hourly_rate,
            salary,
            employment_type
          ),
          attendance_system_configs (
            system_name,
            system_vendor
          )
        `)?.gte('attendance_date', payPeriodStart)?.lte('attendance_date', payPeriodEnd);

      if (departmentId) {
        query = query?.eq('employees.department_id', departmentId);
      }

      const { data: attendanceRecords, error } = await query;

      if (error) throw error;

      // Group by employee
      const employeeAttendance = {};
      
      attendanceRecords?.forEach(record => {
        const empId = record?.employee_id;
        if (!employeeAttendance?.[empId]) {
          employeeAttendance[empId] = {
            employee: record?.employees,
            records: [],
            totalRegularHours: 0,
            totalOvertimeHours: 0,
            totalHours: 0,
            daysWorked: 0,
            source: record?.attendance_system_configs?.system_vendor || 'Manual'
          };
        }
        
        employeeAttendance?.[empId]?.records?.push(record);
        employeeAttendance[empId].totalRegularHours += record?.regular_hours || 0;
        employeeAttendance[empId].totalOvertimeHours += record?.overtime_hours || 0;
        employeeAttendance[empId].totalHours += record?.total_hours || 0;
        employeeAttendance[empId].daysWorked += 1;
      });

      // Create payroll entries
      const payrollEntries = [];
      const conversionResults = {
        payPeriodStart,
        payPeriodEnd,
        totalEmployees: Object.keys(employeeAttendance)?.length,
        processedEmployees: 0,
        totalRegularHours: 0,
        totalOvertimeHours: 0,
        totalGrossPay: 0,
        hikvisionRecords: 0,
        manualRecords: 0,
        errors: []
      };

      for (const [empId, data] of Object.entries(employeeAttendance)) {
        try {
          const employee = data?.employee;
          const hourlyRate = employee?.hourly_rate || (employee?.salary / 173.33); // Monthly salary to hourly
          const overtimeRate = hourlyRate * 1.5; // 1.5x for overtime

          const regularPay = data?.totalRegularHours * hourlyRate;
          const overtimePay = data?.totalOvertimeHours * overtimeRate;
          const grossPay = regularPay + overtimePay;

          payrollEntries?.push({
            employee_id: empId,
            pay_period_start: payPeriodStart,
            pay_period_end: payPeriodEnd,
            regular_hours: data?.totalRegularHours,
            overtime_hours: data?.totalOvertimeHours,
            total_hours: data?.totalHours,
            days_worked: data?.daysWorked,
            hourly_rate: hourlyRate,
            overtime_rate: overtimeRate,
            regular_pay: regularPay,
            overtime_pay: overtimePay,
            gross_pay: grossPay,
            data_source: data?.source,
            attendance_records_count: data?.records?.length,
            created_at: new Date()?.toISOString()
          });

          conversionResults.processedEmployees++;
          conversionResults.totalRegularHours += data?.totalRegularHours;
          conversionResults.totalOvertimeHours += data?.totalOvertimeHours;
          conversionResults.totalGrossPay += grossPay;

          if (data?.source === 'Hikvision') {
            conversionResults.hikvisionRecords += data?.records?.length;
          } else {
            conversionResults.manualRecords += data?.records?.length;
          }
        } catch (err) {
          conversionResults?.errors?.push({
            employeeId: empId,
            error: err?.message
          });
        }
      }

      // Insert payroll entries (if payroll_entries table exists)
      // This would be handled by the payroll processing engine

      return {
        success: true,
        ...conversionResults,
        payrollEntries
      };
    } catch (error) {
      console.error('Failed to convert timesheets to payroll:', error);
      throw error;
    }
  }
};

export default timesheetService;
